import {
  Component,
  EventEmitter,
  Output,
  signal,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FleetService } from '../../services/fleet.service';
import { FileUploadDropzoneComponent } from '../../../shared/components/file-upload-dropzone/file-upload-dropzone.component';
import { FleetPlateInput } from '../../models/fleet.model';
import { FLEET_PLATE_TEMPLATE_CSV, readFleetPlateSheet } from '../../utils/fleet-plate-sheet.util';

export interface LicensePlateImportResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: {
    insertedPlates: {
      id: string;
      fleet_id: string;
      license_plate: string;
      state?: string | null;
      status: boolean;
      created_at: string;
      updated_at: string;
    }[];
    skipped: string[];
    invalid: {
      plate: string;
      state: string | null;
      reason: string;
    }[];
  };
}

/**
 * Modal component for uploading, validating, previewing, and importing license plate lists (CSV or XLSX) into a fleet.
 * @component
 * @selector app-add-license-plates-modal
 * @example
 *   <app-add-license-plates-modal
 *     [fleetId]="'fleet-123'"
 *     [fleetName]="'My Fleet'"
 *     (close)="onModalClose()"
 *     (success)="onImportSuccess($event)"
 *     (viewFile)="onPreviewFile($event)"
 *   ></app-add-license-plates-modal>
 * @input {string} fleetId - Identifier of the fleet to which license plates will be imported.
 * @input {string} fleetName - Human-readable fleet name used in the UI and in emitted preview payloads.
 * @output {EventEmitter<void>} close - Emitted when the modal is closed or the flow is cancelled.
 * @output {EventEmitter<LicensePlateImportResponse>} success - Emitted when import completes successfully with the service response.
 * @output {EventEmitter<{fleetName: string, licensePlates: FleetPlateInput[]}>} viewFile - Emitted when the parsed file is requested for preview; payload contains the file name and the parsed plates with their states.
 *
 * Purpose:
 *   Accepts a CSV or XLSX file with a "License Plate" column (or "Plate Number") and an optional
 *   "State" column (or "State Code"), validates it, lets the user preview the parsed rows, and
 *   imports them via FleetService. Blank states are filled server-side from the fleet's
 *   locations; rows with a non-US state are reported back as invalid.
 *
 * Key inputs / state:
 *   - fleetId, fleetName: context for the import operation
 *   - selectedFile: reactive signal holding the currently selected File or null
 *   - isUploading: reactive signal indicating an ongoing import/upload operation
 *   - error: reactive signal carrying validation or processing error messages
 *   - isValidFile: reactive signal indicating whether the selected file passed validation
 *
 * Important methods:
 *   - handleFile(file: File): Called from app-file-upload-dropzone's (fileSelected) output.
 *       Validates file type (CSV/XLSX) and size (max 10MB), then parses the file to validate it.
 *   - onImport(): Parses the selected file and sends its rows to FleetService.importLicensePlates.
 *   - onDownloadTemplate(): Downloads the CSV template (License Plate, State).
 *   - onRemoveFile(): Clears the selected file and resets validation/error states.
 *   - onViewFile(): Parses the file for preview and emits viewFile.
 */
@Component({
  selector: 'app-add-license-plates-modal',
  standalone: true,
  imports: [CommonModule, FileUploadDropzoneComponent],
  templateUrl: './add-license-plates-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-license-plates-modal.component.scss'],
})
export class AddLicensePlatesModalComponent {
  @Input() fleetId = '';
  @Input() fleetName = '';
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<LicensePlateImportResponse>();
  @Output() viewFile = new EventEmitter<{ fleetName: string; licensePlates: FleetPlateInput[] }>();

  // File upload state
  readonly selectedFile = signal<File | null>(null);
  readonly isUploading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  // File validation
  readonly isValidFile = signal<boolean>(false);

  constructor(private fleetService: FleetService) {}

  onClose(): void {
    this.close.emit();
  }

  /**
   * Handle a file picked or dropped via app-file-upload-dropzone: validate type and size,
   * update component state, and parse it to validate its contents.
   * @param {File} file - The file to be validated and processed.
   */
  handleFile(file: File): void {
    this.error.set(null);
    this.selectedFile.set(file);

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!validTypes.includes(file.type)) {
      this.error.set('Please upload a CSV or XLSX file');
      this.isValidFile.set(false);
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      this.error.set('File size must be less than 10MB');
      this.isValidFile.set(false);
      return;
    }

    this.readPlates(file)
      .then(() => {
        if (this.selectedFile() !== file) {
          return undefined; // superseded by a newer selection/removal
        }
        this.isValidFile.set(true);
        this.error.set(null);
        return undefined;
      })
      .catch((err: Error) => {
        if (this.selectedFile() !== file) {
          return; // superseded by a newer selection/removal
        }
        this.error.set(err.message || 'Error reading file');
        this.isValidFile.set(false);
      });
  }

  /**
   * Import license plates from the currently selected file, handling upload state and result events.
   */
  onImport(): void {
    if (!this.isValidFile() || !this.selectedFile()) {
      return;
    }

    this.isUploading.set(true);
    this.error.set(null);

    this.readPlates(this.selectedFile()!)
      .then(plates => {
        this.fleetService.importLicensePlates(this.fleetId, plates).subscribe({
          next: (response: LicensePlateImportResponse) => {
            this.isUploading.set(false);
            this.success.emit(response);
            this.onClose();
          },
          error: error => {
            this.isUploading.set(false);
            this.error.set(error.message || 'Failed to import license plates');
          },
        });
        return undefined;
      })
      .catch(() => {
        this.isUploading.set(false);
        this.error.set('Error processing file for import');
      });
  }

  onDownloadTemplate(): void {
    const blob = new Blob([FLEET_PLATE_TEMPLATE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'license-plates-template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  onRemoveFile(): void {
    this.selectedFile.set(null);
    this.isValidFile.set(false);
    this.error.set(null);
  }

  /**
   * Parse the selected file and emit its rows for preview; sets an error message on failure.
   */
  onViewFile(): void {
    if (this.selectedFile()) {
      this.readPlates(this.selectedFile()!)
        .then(plates => {
          this.viewFile.emit({
            fleetName: this.selectedFile()?.name || 'File',
            licensePlates: plates,
          });
          return undefined;
        })
        .catch(() => {
          this.error.set('Error parsing file for preview');
        });
    }
  }

  /** Read a CSV or XLSX file and parse its plate rows (rejects with a user-facing message). */
  private readPlates(file: File): Promise<FleetPlateInput[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          resolve(readFleetPlateSheet(e.target?.result as ArrayBuffer));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsArrayBuffer(file);
    });
  }
}
